import { __spreadArrays } from "tslib";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePrefix } from '../ConfigProvider';
import { fillRef } from '.';
// 使用 shared.less 里面的动画样式
export function useAnimationCls() {
    var spinCls = usePrefix('spin');
    return { spinCls: spinCls };
}
/** 管理 focus 状态 */
export function useFocused(extraHandlers, initialValue) {
    if (initialValue === void 0) { initialValue = false; }
    var _a = extraHandlers || {}, onFocus = _a.onFocus, onBlur = _a.onBlur;
    var _b = useState(initialValue), focused = _b[0], setFocused = _b[1];
    var handleFocus = useCallback(function (e) {
        setFocused(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }, [onFocus]);
    var handleBlur = useCallback(function (e) {
        setFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    }, [onBlur]);
    var focusedHandlers = useMemo(function () { return ({ onFocus: handleFocus, onBlur: handleBlur }); }, [handleFocus, handleBlur]);
    return { focused: focused, focusedHandlers: focusedHandlers };
}
/** 将多个 ref 合并成一个 */
export function useRefs() {
    var refs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        refs[_i] = arguments[_i];
    }
    var mergedRef = useCallback(function (value) {
        refs.forEach(function (ref) {
            if (ref != null) {
                fillRef(ref, value);
            }
        });
    }, refs); // eslint-disable-line react-hooks/exhaustive-deps
    return mergedRef;
}
/** 支持受控或不受控输入的 value 逻辑 */
export function useValue(_a) {
    var valueFromProps = _a.valueFromProps, defaultValue = _a.defaultValue, onChange = _a.onChange;
    var initialValue = valueFromProps !== undefined ? valueFromProps : defaultValue;
    var _b = useState(initialValue), innerValue = _b[0], setInnerValue = _b[1];
    var value = valueFromProps !== undefined ? valueFromProps : innerValue;
    var setValue = useCallback(function (v) {
        var restV = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            restV[_i - 1] = arguments[_i];
        }
        setInnerValue(v);
        onChange === null || onChange === void 0 ? void 0 : onChange.apply(void 0, __spreadArrays([v], restV));
    }, [onChange]);
    return { value: value, setValue: setValue };
}
// 第一次加载不触发，之后的每次 rerender 都触发
export function useUpdate(effect, deps) {
    var didMountRef = useRef(false);
    useEffect(function () {
        if (didMountRef.current === false) {
            didMountRef.current = true;
            return;
        }
        return effect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
/** 获取当前组件实例是否存活（是否 mounted）的信息 */
export function useIsMounted() {
    var isMountedRef = useRef(false);
    useEffect(function () {
        isMountedRef.current = true;
        return function () {
            isMountedRef.current = false;
        };
    }, []);
    return isMountedRef;
}
/**
 * 更安全的 useState，防止组件 unmount 后仍然被 setState 导致报错
 * > 使用此方法只能 suppress 错误信息，潜在的内存泄漏问题仍无法避免，需要确定用法后使用
 */
export function useSafeState(initial) {
    var isMountedRef = useIsMounted();
    var _a = useState(initial), state = _a[0], setState = _a[1];
    var setSafeState = useCallback(function (nextState) {
        if (isMountedRef.current) {
            setState(nextState);
        }
    }, [isMountedRef]);
    return [state, setSafeState];
}
//# sourceMappingURL=hooks.js.map